/**
 * Detects whether the host page is currently showing a dark theme.
 *
 * `prefers-color-scheme` only reports the operating system setting, so a site
 * with its own light/dark toggle can be dark while the OS is light. Nearly
 * every such site signals the choice on <html> or <body> in one of a few
 * conventional ways, so we read those first and fall back to the OS.
 */

export type HostTheme = "light" | "dark";

/** Class names that conventionally mean "dark theme is active". */
const DARK_CLASS =
  /(^|\s)(dark|dark-mode|theme-dark|is-dark|night|wp-dark-mode-active|darkluplite-dark-mode-enabled|darklup-dark-mode-enabled|drdt-dark-mode|astra-dark-mode-enable)(\s|$)/i;
const LIGHT_CLASS = /(^|\s)(light|light-mode|theme-light|is-light)(\s|$)/i;

/** Attributes sites use to publish the active theme. */
const THEME_ATTRS = [
  "data-theme",
  "data-color-scheme",
  "data-mode",
  "data-bs-theme", // Bootstrap 5.3
  "color-scheme",
  "data-wp-dark-mode-active", // WP Dark Mode, on <html>
];

/**
 * Attributes whose value only has to *contain* "dark". Blocksy matches its
 * own frontend CSS with [data-color-mode*="dark"], and uses values like
 * "os-default" that an equality check would miss.
 */
const SUBSTRING_ATTRS = ["data-color-mode"];

/** True when the page explicitly says it defers to the operating system. */
function defersToSystem(el: Element | null): boolean {
  if (!el) return false;
  for (const attr of [...THEME_ATTRS, ...SUBSTRING_ATTRS]) {
    const value = el.getAttribute(attr)?.trim().toLowerCase();
    if (!value) continue;
    if (value.includes("os-default") || value === "auto" || value === "system") {
      return true;
    }
  }
  return false;
}

function fromElement(el: Element | null): HostTheme | null {
  if (!el) return null;

  for (const attr of THEME_ATTRS) {
    const value = el.getAttribute(attr)?.trim().toLowerCase();
    if (!value) continue;
    // Boolean-style flags, e.g. data-wp-dark-mode-active="true".
    if (attr.endsWith("-active")) {
      if (value === "true") return "dark";
      if (value === "false") return "light";
      continue;
    }
    // "auto"/"system" means the site is deferring to the OS, so keep looking.
    if (value === "dark") return "dark";
    if (value === "light") return "light";
  }

  for (const attr of SUBSTRING_ATTRS) {
    const value = el.getAttribute(attr)?.trim().toLowerCase();
    if (!value) continue;
    // "os-default" defers to the system, so it is not a decision on its own.
    if (value.includes("os-default") || value.includes("auto")) continue;
    if (value.includes("dark")) return "dark";
    if (value.includes("light")) return "light";
  }

  const className = el.className;
  // SVG elements expose className as an object rather than a string.
  if (typeof className === "string") {
    if (DARK_CLASS.test(className)) return "dark";
    if (LIGHT_CLASS.test(className)) return "light";
  }

  return null;
}

/**
 * Reads the page's actual painted background and decides whether it is dark.
 *
 * This is the ground truth: it works no matter how the site themes itself  
 * a class we do not recognise, a wrapper element, CSS variables, or inline
 * styles. Walks up from the body until it finds a non-transparent background,
 * because body is often transparent with the colour set on html.
 */
function fromRenderedBackground(): HostTheme | null {
  if (typeof getComputedStyle !== "function") return null;

  // Many themes WordPress ones especially leave body transparent and
  // paint a wrapper instead, so those are sampled before falling back to the
  // document elements.
  const wrappers = WRAPPER_SELECTORS.map((sel) =>
    document.querySelector(sel),
  ).filter(Boolean) as Element[];
  const candidates: Array<Element | null> = [
    document.body,
    ...wrappers,
    document.documentElement,
  ];
  for (const el of candidates) {
    if (!el) continue;
    const bg = getComputedStyle(el).backgroundColor;
    const rgb = parseRgb(bg);
    // Skip fully transparent layers they show whatever is behind them.
    if (!rgb || rgb.a === 0) continue;
    // Rec. 601 luma: cheap and good enough to tell light from dark.
    const luma = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return luma < 128 ? "dark" : "light";
  }
  return null;
}

/** Parses the rgb()/rgba() string getComputedStyle returns. */
function parseRgb(
  value: string,
): { r: number; g: number; b: number; a: number } | null {
  const m = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?/i.exec(
    value,
  );
  if (!m) return null;
  return {
    r: Number(m[1]),
    g: Number(m[2]),
    b: Number(m[3]),
    a: m[4] === undefined ? 1 : Number(m[4]),
  };
}

/**
 * Content wrappers that commonly carry the page background when body does
 * not. Ordered outermost-first, since an outer wrapper is the better proxy
 * for "the page".
 */
const WRAPPER_SELECTORS = [
  ".wp-site-blocks", // WordPress block themes
  "#page",
  ".site",
  "#wrapper",
  "#main",
  "main",
];

/** True when the OS is set to dark. */
function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/**
 * Resolves the host page's theme: an explicit signal on <html> or <body>
 * wins, otherwise the OS preference.
 */
export function detectHostTheme(): HostTheme {
  // 1. A signal the site publishes deliberately the most reliable.
  const explicit =
    fromElement(document.documentElement) ?? fromElement(document.body);
  if (explicit) return explicit;

  // 2. A page that says it follows the system means it: skip the painted
  //    background, which may not reflect the choice yet at first paint.
  if (
    defersToSystem(document.documentElement) ||
    defersToSystem(document.body)
  ) {
    return systemPrefersDark() ? "dark" : "light";
  }

  // 3. What the page actually looks like. Catches every theming mechanism,
  //    including ones that never touch html or body attributes.
  const painted = fromRenderedBackground();
  if (painted) return painted;

  // 4. Nothing to go on: fall back to the operating system.
  return systemPrefersDark() ? "dark" : "light";
}

/**
 * Calls back whenever the host theme changes a site toggling a class, or
 * the OS switching. Returns a function that stops watching.
 */
export function watchHostTheme(
  onChange: (theme: HostTheme) => void,
): () => void {
  let current = detectHostTheme();

  const check = () => {
    const next = detectHostTheme();
    if (next !== current) {
      current = next;
      onChange(next);
    }
  };

  // Site toggles mutate class, a data attribute, or an inline style on
  // <html> or <body>. Watching style as well catches themes driven purely by
  // CSS variables, which change no class at all.
  const observer = new MutationObserver(check);
  const options: MutationObserverInit = {
    attributes: true,
    attributeFilter: ["class", "style", ...THEME_ATTRS],
  };
  observer.observe(document.documentElement, options);
  if (document.body) observer.observe(document.body, options);

  // An explicit escape hatch for sites we cannot detect automatically:
  //   window.dispatchEvent(new CustomEvent("minaya:theme", {
  //     detail: { theme: "dark" },
  //   }));
  // Passing no detail re-runs detection instead of forcing a value.
  const onManual = (event: Event) => {
    const detail = (event as CustomEvent<{ theme?: HostTheme }>).detail;
    const forced = detail?.theme;
    if (forced === "dark" || forced === "light") {
      if (forced !== current) {
        current = forced;
        onChange(forced);
      }
      return;
    }
    check();
  };
  window.addEventListener("minaya:theme", onManual);

  // WP Dark Mode dispatches this on document with the new state attached.
  const onWpDarkMode = (event: Event) => {
    const active = (event as CustomEvent<{ isActive?: boolean }>).detail
      ?.isActive;
    const next: HostTheme | null =
      active === true ? "dark" : active === false ? "light" : null;
    if (next && next !== current) {
      current = next;
      onChange(next);
      return;
    }
    check();
  };
  document.addEventListener("wp_dark_mode", onWpDarkMode);

  // Droit announces initialisation; re-read rather than trusting a payload.
  window.addEventListener("DroitDarkModeInit", check);

  // The OS can change under us while the site has no explicit preference.
  const media =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  media?.addEventListener("change", check);

  return () => {
    observer.disconnect();
    media?.removeEventListener("change", check);
    window.removeEventListener("minaya:theme", onManual);
    document.removeEventListener("wp_dark_mode", onWpDarkMode);
    window.removeEventListener("DroitDarkModeInit", check);
  };
}
